/**
 * Comutatorul de coloane.
 *
 * Feedbackul colegelor cere trei coloane noi — Sold, Stoc și Valută — peste cele șapte existente.
 * Zece coloane fixe pe un laptop de 1366px fac grila ilizibilă, iar coloana Furnizor, cea mai
 * importantă, se strânge prima. Aici fiecare își face ecranul.
 *
 * Alegerea intră în URL, deci se trimite prin link și supraviețuiește unui reload — la fel ca
 * filtrele. Coloanele fixe (selecție, stare, furnizor, sumă, note) nu apar aici: fără ele ecranul
 * nu mai răspunde la întrebarea lui, „cât am azi, cui plătesc, ce marchez".
 */

import { useEffect, useRef, useState } from 'react';
import { Columns3 } from 'lucide-react';

import { COMUTABILE, IMPLICITE, type CheieColoana, type DefinitieColoana } from './coloane.ts';

export interface PropsComutatorColoane {
  /** Coloanele active acum, calculate. */
  active: readonly DefinitieColoana[];
  /** Ce a ales omul explicit; `undefined` = implicitele. */
  alese: readonly string[] | undefined;
  onSchimba: (chei: readonly CheieColoana[] | undefined) => void;
}

export function ComutatorColoane({ active, alese, onSchimba }: PropsComutatorColoane) {
  const [deschis, setDeschis] = useState(false);
  const refPanou = useRef<HTMLDivElement>(null);

  // Click în afară închide. Fără asta, panoul rămâne peste grilă și acoperă exact rândurile pe
  // care tocmai ai vrut să le vezi.
  useEffect(() => {
    if (!deschis) return;
    function laClick(ev: MouseEvent): void {
      if (refPanou.current && !refPanou.current.contains(ev.target as Node)) setDeschis(false);
    }
    document.addEventListener('mousedown', laClick);
    return () => document.removeEventListener('mousedown', laClick);
  }, [deschis]);

  const activeSet = new Set(active.map((c) => c.cheie));
  const optionaleActive = COMUTABILE.filter((c) => activeSet.has(c.cheie)).length;

  function comuta(cheie: CheieColoana): void {
    const urmator = new Set<CheieColoana>(
      active.map((c) => c.cheie).filter((k) => k !== 'selectie'),
    );
    if (urmator.has(cheie)) urmator.delete(cheie);
    else urmator.add(cheie);
    onSchimba(['selectie', ...urmator]);
  }

  return (
    <div ref={refPanou} className="relative">
      <button
        type="button"
        className="hit-24 chip"
        aria-expanded={deschis}
        aria-haspopup="true"
        onClick={() => setDeschis((d) => !d)}
      >
        <Columns3 size={12} aria-hidden="true" />
        Coloane
        <span className="num text-ink-3">
          {optionaleActive}/{COMUTABILE.length}
        </span>
      </button>

      {deschis ? (
        <div
          role="group"
          aria-label="Coloane afișate"
          className="dialog absolute right-0 z-40 mt-1 flex w-72 flex-col gap-1 rounded-[var(--radius-md)] p-2"
        >
          {COMUTABILE.map((col) => (
            <label key={col.cheie} className="hit-24 flex items-start gap-2 px-1 text-12">
              <input
                type="checkbox"
                className="mt-1"
                checked={activeSet.has(col.cheie)}
                onChange={() => comuta(col.cheie)}
              />
              <span className="flex flex-col">
                <span>{col.eticheta}</span>
                {col.descriere ? (
                  <span className="text-11 text-ink-3">{col.descriere}</span>
                ) : null}
              </span>
            </label>
          ))}

          <button
            type="button"
            className="hit-24 chip mt-1 self-start"
            disabled={alese === undefined}
            onClick={() => onSchimba(undefined)}
            title={`Implicit: ${IMPLICITE.filter((k) => k !== 'selectie').join(', ')}`}
          >
            Înapoi la implicit
          </button>
        </div>
      ) : null}
    </div>
  );
}
